import { Check } from "lucide-react";
import { useEffect, useState } from "react";
import { formatCurrency } from "@/components/deal-metrics/shared";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";

interface SalaryBenefitsData {
  baseSalary: number | null;
  annualBonusPercent: number | null;
  equityPercent: number | null;
  benefits: {
    healthInsurance: boolean;
    shortTermDisability: boolean;
    longTermDisability: boolean;
    appleComputer: boolean;
    retirement401k: boolean;
  };
}

export function SalaryBenefitsTab({ ownerId }: { ownerId: string }) {
  const [data, setData] = useState<SalaryBenefitsData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await fetch(`/api/owners/${ownerId}/salary-benefits`);
        if (res.ok && !cancelled) setData(await res.json());
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [ownerId]);

  if (loading) {
    return (
      <Card>
        <CardContent className="py-8">
          <Skeleton className="h-6 w-40" />
        </CardContent>
      </Card>
    );
  }

  if (!data) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-muted-foreground">
          Could not load salary and benefits information.
        </CardContent>
      </Card>
    );
  }

  const benefitFields: { key: keyof SalaryBenefitsData["benefits"]; label: string }[] = [
    { key: "healthInsurance", label: "Health Insurance" },
    { key: "shortTermDisability", label: "Short Term Disability" },
    { key: "longTermDisability", label: "Long Term Disability" },
    { key: "appleComputer", label: "Apple Computer" },
    { key: "retirement401k", label: "401(k)" },
  ];
  const enrolledFields = benefitFields.filter(({ key }) => data.benefits[key]);

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
      <Card>
        <CardHeader>
          <CardTitle className="text-sm font-medium uppercase tracking-wider text-muted-foreground">
            Compensation
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <p className="font-heading text-3xl font-bold tracking-tight">
              {data.baseSalary != null ? (
                formatCurrency(data.baseSalary)
              ) : (
                <span className="text-muted-foreground">—</span>
              )}
            </p>
            <p className="mt-1 text-xs text-muted-foreground">Base Salary (Annual)</p>
          </div>
          {data.annualBonusPercent != null && (
            <div className="flex items-center justify-between border-t pt-3">
              <span className="text-sm">Annual Bonus</span>
              <span className="text-sm font-medium tabular-nums">{data.annualBonusPercent}%</span>
            </div>
          )}
          {data.equityPercent != null && (
            <div className="flex items-center justify-between border-t pt-3">
              <span className="text-sm">Equity</span>
              <span className="text-sm font-medium tabular-nums">{data.equityPercent}%</span>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-sm font-medium uppercase tracking-wider text-muted-foreground">
            Benefits Enrollment
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2.5">
          {enrolledFields.length === 0 ? (
            <p className="text-sm text-muted-foreground">No benefits enrolled.</p>
          ) : (
            enrolledFields.map(({ key, label }) => (
              <div key={key} className="flex items-center justify-between">
                <span className="text-sm">{label}</span>
                <div className="flex h-6 w-6 items-center justify-center rounded border-2 border-primary bg-primary text-primary-foreground">
                  <Check className="h-3.5 w-3.5" />
                </div>
              </div>
            ))
          )}
        </CardContent>
      </Card>
    </div>
  );
}
